import DraggableMultiValue from 'client/js/common/components/core/PureQueryBuilder/DraggableCompareBy/DraggableMultiValue';
import DraggableMultiValueLabel from 'client/js/common/components/core/PureQueryBuilder/DraggableCompareBy/DraggableMultiValueLabel';
import DroppableValueContainer from 'client/js/common/components/core/PureQueryBuilder/DraggableCompareBy/DroppableValueContainer';
import TableModeSwitcher from 'client/js/main/screens/ScreenRoiHub/analytics/TableModeSwitcher/TableModeSwitcher';
import { DomainAnalyticsService } from 'client/js/main/services/analytics/DomainAnalyticsService';
import type { RootState } from 'client/js/redux';
import * as React from 'react';
import { connect } from 'react-redux';
import type { RouteComponentProps } from 'react-router';
import { withRouter } from 'react-router';
import { ContextsService } from '../../../../../../shared/Contexts.service';
import { DARKLY_PRODUCT_FLAGS } from '../../../../../../shared/constants';
import type { DruidExplanation } from '../../../../../../shared/types/druidTypes';
import type { ReportActiveMode } from '../../../../../../shared/types/roi-hub/TableModeSwitcher.types';
import { selectLightweightFiltersEnabled } from '../../../../main/components/analytics/LightweightStickyBar/LightweightFilters.selectors';
import { isVidaliaProductEnabled } from '../../../../main/selectors/ProductSelectors';
import { generateCsvExportModalOpenPathName } from '../../../../main/services/CsvExportQueryService';
import { ImageService } from '../../../../main/services/ImageService/ImageService';
import type { Filter, Filters } from '../../../../main/types/druidTypes';
import { GranularitySelector, HoverButton, Tooltip } from '../../core';
import CompareBy from './CompareBy';
import DimensionOption from './DimensionOption';
import FilterRows from './FilterRows';
import MultiValueLabel from './MultiValueLabel';

/**
 * PureQueryBuilder: A component that provides the User Interface for constructing a Cenarius Query via the Dashboard.
 * DruidQueryBuilder differs from PureQueryBuilder because it is stateful and contains logic that is specific to constructing queries on pages where a query builder must appear stacked above a timeseries or table (e.g. ScreenAdsAnalytics, ScreenEmailAnalytics).
 * Since the User Interface for constructing queries should be independent of where and how it is used, PureQueryBuilder was implemented.
 * The use case was cheifly to place a query builder in a modal (e.g. Cohorts).
 */
type Props = {
  dimensions: Array<string>;
  eventSelect?: React.ReactElement<any>;
  cohortSelect?: React.ReactElement<any>;
  fraudSelect?: React.ReactElement<any>;
  hideCompareBy?: boolean;
  styleVariant?: string;
  dimensionsToCompareBy: Array<string>;
  dimensionsToFilterBy: Array<string>;
  maxDimensions: number;
  minDimensions: number;
  filter?: Filter;
  requiredFilter?: Filter;
  allFilters: Filters;
  unique?: boolean;
  updateUnique?: () => void;
  disableUniqueToggle?: boolean;
  organic?: boolean;
  updateOrganic?: () => void;
  disableOrganicToggle?: boolean;
  webOnly?: boolean;
  updateWebOnly?: () => void;
  disableWebOnlyToggle?: boolean;
  hasExportButton?: boolean;
  hasUniqueToggle?: boolean;
  hasOrganicToggle?: boolean;
  hasWebOnlyToggle?: boolean;
  hasFraudDescriptionToggle?: boolean;
  hasSplitSkanDataToggle?: boolean;
  splitSkanData?: boolean;
  updateSkanSplit?: (arg0: Record<string, any>) => void;
  showFraudDescription?: boolean;
  toggleFraudDescription?: (arg0: Record<string, any>) => void;
  testid: string;
  updateQuery: (arg0: Record<string, any>) => void;
  title?: string;
  tooltip?: string | React.ReactElement<any>;
  updateFraudTypeOnCompareByChange?: () => void;
  customLabelMap?: Record<string, any>;
  createFilterValues?: boolean;
  fetchFilters: (arg0: string) => void;
  recordLimit?: number;
  updateGranularity?: (arg0: Record<string, any>) => void;
  granularity?: string;
  updateBlockedEventIndicator?: (arg0: string) => void;
  context: string;
  query?: Record<string, any>;
  runFullReportButton?: React.ReactElement<any>;
  namespace?: string;
  lightweightFiltersEnabled: boolean;
  isActivationEnabled: boolean;
  cohortsUIRefreshEnabled: boolean;
  explanation?: DruidExplanation;
  draggableCompareBy?: boolean;
  activeMode?: ReportActiveMode;
  updateActiveMode?: (activeMode: ReportActiveMode) => void;
  hideTableModeSwitcher?: boolean;
} & RouteComponentProps;

class PureQueryBuilder extends React.PureComponent<Props> {
  domainAnalytics = DomainAnalyticsService.build();

  renderTitle = () => {
    const props = this.props;

    if (!props.title) {
      return null;
    }

    if (props.tooltip) {
      return (
        <div className="analytics__title">
          <Tooltip body={props.tooltip}>{props.title}</Tooltip>
        </div>
      );
    }

    return <div className="analytics__title">{props.title}</div>;
  };

  isUsingDrilldown = () => {
    return this.domainAnalytics.isDrillDownEnabled(this.props.context);
  };

  shouldShowGranularitySelector = () => {
    if (this.isUsingDrilldown()) {
      return false;
    }
    const { context } = this.props;
    const isTableContext = ContextsService.isTableContext(context);
    const isTimeSeriesContext = ContextsService.isTimeSeriesContext(context);
    return isTableContext || isTimeSeriesContext;
  };

  getFieldComponents = () => {
    const components: Record<string, any> = {};

    if (this.props.draggableCompareBy) {
      components.ValueContainer = DroppableValueContainer;
      components.MultiValue = DraggableMultiValue;
      components.MultiValueLabel = DraggableMultiValueLabel;
    }

    if (this.props.explanation) {
      components.DimensionOption = DimensionOption;
      components.MultiValueLabel = (props) => <MultiValueLabel {...props} draggable={this.props.draggableCompareBy} />;
    }

    return components;
  };

  renderTableSwitcher = () => {
    if (this.props.hideTableModeSwitcher || !this.props.activeMode || !this.props.updateActiveMode) {
      return null;
    }
    return (
      <TableModeSwitcher
        context={this.props.context}
        activeMode={this.props.activeMode}
        updateActiveMode={this.props.updateActiveMode}
      />
    );
  };

  render() {
    const props = this.props;
    return (
      <div className="analytics-filters__wrapper">
        {props.cohortSelect &&
          React.cloneElement(props.cohortSelect, {
            updateQuery: props.updateQuery,
          })}
        {this.renderTitle()}
        <div className="analytics-filters">
          {props.fraudSelect &&
            React.cloneElement(props.fraudSelect, {
              dimensions: props.dimensions,
              updateQuery: props.updateQuery,
            })}
          <div className="analytics-filters__row h_ai-c">
            {props.eventSelect &&
              React.cloneElement(props.eventSelect, {
                updateQuery: props.updateQuery,
              })}
            {!props.hideCompareBy && (
              <CompareBy
                allFilters={props.allFilters}
                dimensions={props.dimensions}
                dimensionsToDisplay={props.dimensionsToCompareBy}
                maxDimensions={props.maxDimensions}
                minDimensions={props.minDimensions}
                updateFraudTypeOnCompareByChange={props.updateFraudTypeOnCompareByChange}
                updateQuery={props.updateQuery}
                customLabelMap={props.customLabelMap}
                recordLimit={props.recordLimit}
                context={props.context}
                granularity={props.granularity}
                query={props.query}
                organic={props.organic}
                explanation={props.explanation}
                draggable={props.draggableCompareBy}
                components={this.getFieldComponents()}
              />
            )}
            {props.runFullReportButton || null}
            {props.lightweightFiltersEnabled &&
              this.shouldShowGranularitySelector() &&
              props.updateGranularity &&
              props.granularity && (
                <GranularitySelector
                  updateGranularity={props.updateGranularity}
                  granularity={props.granularity}
                  context={props.context}
                />
              )}
            {this.renderTableSwitcher()}
            {props.lightweightFiltersEnabled && props.hasExportButton && (
              <div className="download-csv-button">
                <HoverButton
                  to={generateCsvExportModalOpenPathName(window.location)}
                  textShownOnHover="Download CSV"
                  activeChild={<img src={ImageService.getImageUrl('/static/images/ic_download_active.svg')} />}
                  size="no-miw"
                  type="secondary"
                  className="icon-hover-button"
                  testid={`${props.testid}__download-csv`}
                >
                  <img src={ImageService.getImageUrl('/static/images/ic_download.svg')} />
                </HoverButton>
              </div>
            )}
          </div>
          {!props.lightweightFiltersEnabled && (
            <FilterRows
              allFilters={props.allFilters}
              requiredFilter={props.requiredFilter}
              filter={props.filter}
              fetchFilters={props.fetchFilters}
              unique={props.unique}
              updateUnique={props.updateUnique}
              webOnly={props.webOnly}
              updateWebOnly={props.updateWebOnly}
              disableUniqueToggle={props.disableUniqueToggle}
              organic={props.organic}
              updateOrganic={props.updateOrganic}
              disableOrganicToggle={props.disableOrganicToggle}
              disableWebOnlyToggle={props.disableWebOnlyToggle}
              hasFraudDescriptionToggle={props.hasFraudDescriptionToggle}
              showFraudDescription={props.showFraudDescription}
              toggleFraudDescription={props.toggleFraudDescription}
              hasExportButton={props.hasExportButton}
              testid={props.testid}
              hasUniqueToggle={props.hasUniqueToggle}
              hasOrganicToggle={props.hasOrganicToggle}
              hasWebOnlyToggle={props.hasWebOnlyToggle && props.isActivationEnabled}
              hasSplitSkanDataToggle={props.hasSplitSkanDataToggle}
              splitSkanData={props.splitSkanData}
              updateSkanSplit={props.updateSkanSplit}
              dimensionsToDisplay={props.dimensionsToFilterBy}
              updateQuery={props.updateQuery}
              customLabelMap={props.customLabelMap}
              updateGranularity={props.updateGranularity}
              granularity={props.granularity}
              updateBlockedEventIndicator={props.updateBlockedEventIndicator}
              context={props.context}
              dimensions={props.dimensions}
              styleVariant={props.styleVariant}
              namespace={props.namespace}
            />
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: RootState, props) => ({
  lightweightFiltersEnabled: selectLightweightFiltersEnabled(state, props.namespace),
  isActivationEnabled: isVidaliaProductEnabled(state, DARKLY_PRODUCT_FLAGS.ACTIVATION),
});

const ConnectedPureQueryBuilder = withRouter(connect(mapStateToProps)(PureQueryBuilder));
export default ConnectedPureQueryBuilder;
